import { ref, computed, reactive, onUnmounted } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import BulkEditProgressToast from "@/components/BulkEdit/BulkEditProgressToast.vue";
import i18n from "@/libs/i18n";
import axios from "@axios";

// Batch status polling (see batch tracking in submitBulkEdit)
const BATCH_POLL_INTERVAL_MS = 2000;
const BATCH_POLL_MAX_FAILURES = 3;

/**
 * Reusable bulk-edit composable.
 *
 * Payload sent (direct POST, no store). Separates filters from the data to update:
 *   - Manual selection: { [idsKey]: [id, id, ...], filters: {...}, data: {...changedFields} }
 *   - Select all:       { select_all: true, filters: {...}, data: {...changedFields} }
 *
 * `filters` = getSelectAllExtras() (active list filters); always sent.
 * `data`    = only the fields the user changed (!= null).
 *
 * @param {Object} config
 * @param {string}   config.endpoint            - Bulk POST endpoint (e.g. 'v1/unit/bulk-update')
 * @param {Array}    config.fields              - Editable field definitions (see bulkEditConfig.js)
 * @param {string}   [config.idsKey="ids"]      - Ids array key in the payload (e.g. 'id_units')
 * @param {Function} [config.getSelectAllExtras] - () => ({...}) active list filters placed in `filters`
 * @param {string}   [config.batchStatusEndpoint="v1/unit/batches"] - GET endpoint for batch status
 *                                                  ({batchStatusEndpoint}/{batch_id})
 * @param {Function} [config.onSuccess]         - Success callback (e.g. refetchData)
 * @param {Function} [config.onError]           - Error callback
 */
export default function useBulkEdit(config) {
  const toast = useToast();
  const idsKey = config.idsKey || "ids";
  const batchStatusEndpoint = config.batchStatusEndpoint || "v1/unit/batches";

  // --- State ---
  const isBulkEditMode = ref(false);
  const selectedIds = ref([]);
  // When true, the update applies to ALL records matching the current filter
  // (not only the ids selected on the page).
  const selectAllMatching = ref(false);
  // Total records matching the filter. Kept in sync by the view.
  const totalCount = ref(0);
  const isBulkModalOpen = ref(false);
  const isSubmitting = ref(false);
  const formData = ref({});

  // --- Computed ---
  const selectedCount = computed(() =>
    selectAllMatching.value ? totalCount.value : selectedIds.value.length
  );
  const hasSelection = computed(
    () => selectAllMatching.value || selectedIds.value.length > 0
  );

  // --- Mode actions ---
  const toggleBulkEditMode = () => {
    isBulkEditMode.value = !isBulkEditMode.value;
    if (!isBulkEditMode.value) {
      clearSelection();
    }
  };

  const enableBulkEditMode = () => {
    isBulkEditMode.value = true;
  };

  const disableBulkEditMode = () => {
    isBulkEditMode.value = false;
    clearSelection();
  };

  // --- Selection actions ---
  const toggleItem = (id) => {
    // Toggling an individual selection exits "select all" mode.
    selectAllMatching.value = false;
    const index = selectedIds.value.indexOf(id);
    if (index === -1) {
      selectedIds.value = [...selectedIds.value, id];
    } else {
      const copy = [...selectedIds.value];
      copy.splice(index, 1);
      selectedIds.value = copy;
    }
  };

  // Replaces the explicit selection (and disables "select all").
  const setSelectedIds = (ids) => {
    selectAllMatching.value = false;
    selectedIds.value = [...ids];
  };

  // Selects all records matching the filter (cross-pagination).
  const enableSelectAllMatching = () => {
    selectAllMatching.value = true;
    selectedIds.value = [];
  };

  const clearSelection = () => {
    selectAllMatching.value = false;
    selectedIds.value = [];
  };

  // Backward-compatible aliases
  const selectAll = (items) => setSelectedIds(items.map((item) => item.id));
  const deselectAll = () => clearSelection();

  const isSelected = (id) => {
    if (selectAllMatching.value) return true;
    return selectedIds.value.indexOf(id) !== -1;
  };

  // --- Modal actions ---
  const openBulkModal = () => {
    // Initialize formData with null for each field
    const initial = {};
    config.fields.forEach((field) => {
      initial[field.key] = null;
    });
    formData.value = initial;
    isBulkModalOpen.value = true;
  };

  const closeBulkModal = () => {
    isBulkModalOpen.value = false;
    formData.value = {};
  };

  // --- Batch tracking (backend queues the job and returns a batch_id) ---
  // Progress is shown in a persistent toast with a progress bar.
  // `batchToastState` is reactive and passed as a prop to the toast, so its
  // content updates on each poll without needing toast.update().
  const isBatchRunning = ref(false);
  const batchProgress = ref(null);
  const batchToastState = reactive({
    title: "",
    text: "",
    progress: null,
    variant: "primary",
    icon: "RefreshCwIcon",
  });
  let batchTimer = null;
  let batchToastId = null;
  let batchPollFailures = 0;
  // Guards against scheduling/handling further polls once the batch has ended
  // (finished, cancelled, failed tracking, or component unmounted). Prevents
  // overlapping requests and double conclusions from an in-flight response.
  let isBatchConcluded = false;

  const stopBatchPolling = () => {
    if (batchTimer) {
      clearTimeout(batchTimer);
      batchTimer = null;
    }
  };

  // Schedules the next poll only while the batch is still being tracked. Using a
  // self-scheduling timeout (instead of setInterval) guarantees a single request
  // in flight at a time and that no request is issued after the batch concludes.
  const scheduleNextPoll = (batchId) => {
    if (isBatchConcluded) return;
    batchTimer = setTimeout(
      () => pollBatchStatus(batchId),
      BATCH_POLL_INTERVAL_MS
    );
  };

  const dismissBatchToast = () => {
    if (batchToastId !== null) {
      toast.dismiss(batchToastId);
      batchToastId = null;
    }
  };

  // Stops polling, dismisses the progress toast and notifies the result.
  // Idempotent: an already-scheduled or in-flight poll must never conclude twice.
  const concludeBatch = ({ title, variant, icon, timeout }) => {
    if (isBatchConcluded) return;
    isBatchConcluded = true;
    stopBatchPolling();
    dismissBatchToast();
    isBatchRunning.value = false;
    batchProgress.value = null;

    toast(
      {
        component: ToastificationContent,
        props: { title, icon, variant },
      },
      timeout ? { timeout } : undefined
    );
  };

  const pollBatchStatus = async (batchId) => {
    // A poll may have been scheduled before the batch ended; bail out early.
    if (isBatchConcluded) return;
    try {
      const response = await axios.get(`${batchStatusEndpoint}/${batchId}`);
      // The batch may have ended (or unmounted) while this request was in flight.
      if (isBatchConcluded) return;
      const status = response.data?.data ?? response.data;
      batchPollFailures = 0;

      const progress = status?.progress ?? null;
      batchProgress.value = progress;
      batchToastState.progress = progress;

      // The backend reports `finished: true` for both success and cancellation,
      // so the cancelled check must come first.
      if (status?.cancelled) {
        concludeBatch({
          title: i18n.t("bulk_edit.batch_cancelled"),
          variant: "warning",
          icon: "AlertTriangleIcon",
        });
        return;
      }

      if (status?.finished) {
        concludeBatch({
          title: i18n.t("bulk_edit.success"),
          variant: "success",
          icon: "CheckIcon",
        });
        if (config.onSuccess) {
          config.onSuccess(response);
        }
        return;
      }

      // Still running: queue the next poll.
      scheduleNextPoll(batchId);
    } catch (error) {
      if (isBatchConcluded) return;
      // Tolerate transient network errors before giving up on tracking.
      batchPollFailures += 1;
      if (batchPollFailures >= BATCH_POLL_MAX_FAILURES) {
        concludeBatch({
          title: i18n.t("bulk_edit.batch_status_error"),
          variant: "danger",
          icon: "AlertTriangleIcon",
          timeout: 10000,
        });
        if (config.onError) {
          config.onError(error);
        }
        return;
      }
      // Transient failure: retry on the next interval.
      scheduleNextPoll(batchId);
    }
  };

  const startBatchTracking = (batchId, count) => {
    isBatchRunning.value = true;
    isBatchConcluded = false;
    batchProgress.value = null;
    batchPollFailures = 0;

    batchToastState.title = i18n.t("bulk_edit.batch_progress_title");
    batchToastState.text = i18n.t("bulk_edit.batch_progress_text", { count });
    batchToastState.progress = null;
    batchToastState.variant = "primary";

    batchToastId = toast(
      {
        component: BulkEditProgressToast,
        props: { state: batchToastState },
      },
      {
        timeout: false,
        closeButton: false,
        closeOnClick: false,
        draggable: false,
        icon: false,
      }
    );

    // Kicks off the self-scheduling poll loop (each poll queues the next one).
    pollBatchStatus(batchId);
  };

  onUnmounted(() => {
    isBatchConcluded = true;
    stopBatchPolling();
    dismissBatchToast();
  });

  // --- Payload and submit ---
  const buildChangedFields = () => {
    // Only include fields the user changed (not null). Empty strings and
    // empty arrays count as untouched: a cleared text/number input or an
    // emptied multiselect must not be sent (an empty array would mean
    // "replace the list with nothing").
    const changedFields = {};
    Object.entries(formData.value).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        changedFields[key] = value;
      }
    });
    return changedFields;
  };

  const buildPayload = () => {
    const data = buildChangedFields();
    const filters = config.getSelectAllExtras
      ? config.getSelectAllExtras()
      : {};

    if (selectAllMatching.value) {
      return {
        select_all: true,
        filters,
        data,
      };
    }

    return {
      [idsKey]: [...selectedIds.value],
      filters,
      data,
    };
  };

  const hasFormChanges = computed(() => {
    return Object.values(formData.value).some(
      (v) =>
        v !== null &&
        v !== undefined &&
        v !== "" &&
        !(Array.isArray(v) && v.length === 0)
    );
  });

  /**
   * @param {Object} [extraPayload] - Optional extra keys merged into the bulk
   *   payload (e.g. { cameras: { ids: [...] } }). Allows submitting even when
   *   no form field changed (e.g. camera assignment only).
   */
  const submitBulkEdit = async (extraPayload) => {
    if (!hasSelection.value || (!hasFormChanges.value && !extraPayload)) return;

    // Avoid queueing another batch while one is being tracked.
    if (isBatchRunning.value) {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t("bulk_edit.batch_in_progress"),
          icon: "ClockIcon",
          variant: "warning",
        },
      });
      return;
    }

    const payload = { ...buildPayload(), ...(extraPayload || {}) };
    // Capture the count before clearing the selection (for the progress toast).
    const count = selectedCount.value;

    isSubmitting.value = true;
    try {
      const response = await axios.post(config.endpoint, payload);

      // If the backend queued the job, track progress via polling.
      const batchId = response.data?.data?.batch_id;
      if (batchId) {
        closeBulkModal();
        disableBulkEditMode();
        startBatchTracking(batchId, count);
        return response;
      }

      // Synchronous response (no batch): original behavior.
      toast({
        component: ToastificationContent,
        props: {
          title: response.data?.message || "Actualización masiva exitosa",
          icon: "CheckIcon",
          variant: "success",
        },
      });

      if (config.onSuccess) {
        config.onSuccess(response);
      }

      closeBulkModal();
      disableBulkEditMode();
      return response;
    } catch (error) {
      toast(
        {
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              "Error en la actualización masiva",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        },
        { timeout: 10000 }
      );

      if (config.onError) {
        config.onError(error);
      }

      throw error;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    // State
    isBulkEditMode,
    selectedIds,
    selectAllMatching,
    totalCount,
    selectedCount,
    hasSelection,
    isBulkModalOpen,
    isSubmitting,
    formData,
    hasFormChanges,
    isBatchRunning,
    batchProgress,

    // Mode actions
    toggleBulkEditMode,
    enableBulkEditMode,
    disableBulkEditMode,

    // Selection actions
    toggleItem,
    setSelectedIds,
    enableSelectAllMatching,
    clearSelection,
    selectAll,
    deselectAll,
    isSelected,

    // Modal / submit actions
    openBulkModal,
    closeBulkModal,
    buildPayload,
    submitBulkEdit,
  };
}
